import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { APP_EXCLUSIVE_LENGTHS_DAYS, APP_EXCLUSIVE_TIMEZONE, AppExclusiveLengthDays } from '~/consts/appExclusive';

import { useCreateAppExclusiveSchedule } from '~/hooks/apis/offers/useCreateAppExclusiveSchedule';
import { useGetEligibleAppExclusiveOffers } from '~/hooks/apis/offers/useGetEligibleAppExclusiveOffers';
import useDebounced from '~/hooks/useDebounced';

import {
  formatDayLabel,
  formatLengthLabel,
  formatSlotTime,
  getFullSlotDays,
  getMaxPerDay,
  getOfferBlockReason,
  getOnSaleEnd,
  getSlotDayDeals,
  getSlotLengthMismatch,
  todayDate,
} from './appOnlyFlashCalendar';

const DATE_FORMAT = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 300;
// Enough to scan a day's options; the search box narrows the rest.
const ELIGIBLE_OFFER_LIMIT = 50;
// Most deals run one day; 48 hours is picked per deal.
const DEFAULT_LENGTH_DAYS: AppExclusiveLengthDays = 1;

interface Props {
  open: boolean;
  region: string;
  regionName: string;
  /** Sydney calendar day (YYYY-MM-DD) to open on. */
  initialDate: string;
  onClose: () => void;
}

export default function AddAppOnlyFlashDealDialog({ open, region, regionName, initialDate, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [date, setDate] = useState(initialDate);
  const [days, setDays] = useState<AppExclusiveLengthDays>(DEFAULT_LENGTH_DAYS);
  const [search, setSearch] = useState('');
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const debouncedSearch = useDebounced(search.trim(), SEARCH_DEBOUNCE_MS);

  // Each open starts fresh on the day the admin clicked.
  useEffect(() => {
    if (open) {
      setDate(initialDate);
      setDays(DEFAULT_LENGTH_DAYS);
      setSearch('');
      setSelectedOfferId(null);
    }
  }, [open, initialDate]);

  const { data, isFetching, isError, error } = useGetEligibleAppExclusiveOffers(
    { region, date, days, q: debouncedSearch || undefined, limit: ELIGIBLE_OFFER_LIMIT },
    open,
  );
  const createSchedule = useCreateAppExclusiveSchedule();

  const offers = data?.offers ?? [];
  const selectedOffer = offers.find((offer) => offer.offerId === selectedOfferId && offer.canSchedule);
  const maxPerDay = data ? getMaxPerDay(data) : undefined;
  const fullDays = data ? getFullSlotDays(data) : [];
  const fullDates = new Set(fullDays.map((day) => day.date));
  // Days of the slot that already have a deal but still have room, so the admin knows what they'd share with.
  const sharedDays = data ? getSlotDayDeals(data).filter((day) => day.deals.length && !fullDates.has(day.date)) : [];
  // An older svc-offer ignores `days` and returns a 24 hour slot; adding from it would shorten the deal.
  const lengthMismatch = data ? getSlotLengthMismatch(data, days) : undefined;

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    if (value?.isValid()) {
      setDate(value.format(DATE_FORMAT));
      setSelectedOfferId(null);
    }
  };

  const handleDaysChange = (_event: React.MouseEvent<HTMLElement>, value: AppExclusiveLengthDays | null) => {
    // Exclusive toggle: clicking the active option sends null, which would leave no length picked.
    if (value) {
      setDays(value);
      setSelectedOfferId(null);
    }
  };

  const handleSubmit = async () => {
    if (!data || !selectedOffer || lengthMismatch) {
      return;
    }
    try {
      await createSchedule.mutateAsync({
        offerId: selectedOffer.offerId,
        region,
        start: data.slot.start,
        end: data.slot.end,
      });
      enqueueSnackbar(
        `${selectedOffer.name ?? selectedOffer.offerId} scheduled for ${formatSlotTime(data.slot.start)}`,
        {
          variant: 'success',
        },
      );
      onClose();
    } catch (err) {
      enqueueSnackbar(`Could not schedule deal: ${err?.message ?? 'Unknown error'}`, { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="add-app-only-flash-deal-title">
      <DialogTitle id="add-app-only-flash-deal-title">Add App Only Flash deal · {regionName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label="Day"
              format="ddd D MMM YYYY"
              value={dayjs(date, DATE_FORMAT)}
              minDate={dayjs(todayDate(), DATE_FORMAT)}
              onChange={handleDateChange}
              slotProps={{ textField: { required: true, sx: { minWidth: 220 } } }}
            />
            <ToggleButtonGroup
              exclusive
              color="primary"
              value={days}
              onChange={handleDaysChange}
              aria-label="Slot length"
              sx={{ flexShrink: 0 }}
            >
              {APP_EXCLUSIVE_LENGTHS_DAYS.map((length) => (
                <ToggleButton key={length} value={length} sx={{ px: 2 }}>
                  {formatLengthLabel(length)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              label="Search offer name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              fullWidth
            />
          </Stack>

          {data && (
            <Typography variant="body2" color="text.secondary">
              Slot runs {formatSlotTime(data.slot.start)} to {formatSlotTime(data.slot.end)} ({APP_EXCLUSIVE_TIMEZONE}).
              The deal must stay on sale until {formatSlotTime(data.slot.onSaleUntil)}.
            </Typography>
          )}

          {isError && <Alert severity="error">Could not load eligible offers: {error?.message}</Alert>}
          {lengthMismatch && (
            <Alert severity="error">
              Can't add a {formatLengthLabel(days)} deal: {lengthMismatch}.
            </Alert>
          )}
          {fullDays.map((day) => (
            <Alert key={day.date} severity="warning">
              {formatDayLabel(day.date)} already has {day.deals.length} deals in {region} (offers{' '}
              {day.deals.map((deal) => deal.offerId).join(', ')}). Pick another day
              {days > 1 ? ' or a 24h slot' : ''}.
            </Alert>
          ))}
          {sharedDays.map((day) => (
            <Alert key={day.date} severity="info">
              {formatDayLabel(day.date)} has {day.deals.length} of {maxPerDay} deals (offer{' '}
              {day.deals.map((deal) => deal.offerId).join(', ')}). This deal will share the day.
            </Alert>
          ))}

          <Box sx={{ minHeight: 240, maxHeight: 420, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
            {isFetching && !data && (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress size={24} />
              </Stack>
            )}
            {data && offers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No offline flash hotels are on sale for this slot
                {debouncedSearch ? ` matching "${debouncedSearch}"` : ''}.
              </Typography>
            )}
            {offers.length > 0 && (
              <List dense disablePadding>
                {offers.map((offer) => {
                  const blockReason = getOfferBlockReason(offer);
                  const isSelected = offer.offerId === selectedOfferId;
                  return (
                    <ListItemButton
                      key={offer.offerId}
                      selected={isSelected}
                      disabled={!!blockReason}
                      onClick={() => setSelectedOfferId(offer.offerId)}
                      divider
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Radio edge="start" checked={isSelected} disabled={!!blockReason} tabIndex={-1} />
                      </ListItemIcon>
                      <ListItemText
                        primary={offer.name ?? offer.offerId}
                        secondary={[
                          offer.location,
                          blockReason ?? `On sale until ${formatSlotTime(getOnSaleEnd(offer))}`,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>

          {data && data.total > offers.length && (
            <Typography variant="caption" color="text.secondary">
              Showing {offers.length} of {data.total} offers. Search by name to narrow the list.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedOffer || !!lengthMismatch || createSchedule.isPending}
        >
          {createSchedule.isPending ? 'Adding...' : 'Add deal'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
